'use client';
import { useEffect, useRef, useState } from 'react';

// utils
import { useLocation } from '@/context/locationContext';

// types
import { IFloorPlanImage, IHousePlanHit } from '@/lib/search/types';
import { IExteriorStyles } from '@/components/organisms/or-exterior-switcher/or-exterior-switcher.types';
import { getEntriesWithFilter } from '@/lib/contentstack-sdk';
import { ITmHomePlanTemplateCSProps } from '../tm-home-plan-detail.types';

// Ref to track if a fetch is in progress for a given cacheKey
const homePlanCache: Record<
  string,
  {
    data: IHousePlanHit[];
    timestamp: number;
    fallbackImages: IFloorPlanImage[] | null;
  }
> = {};
const homePlanFetchPromises: Record<string, Promise<IHousePlanHit[]> | null> =
  {};
const CACHE_TTL = 1000 * 60 * 5; // 5 minutes

export const getExteriorStyles = (
  homePlanData: IHousePlanHit[],
  designStudioId?: number | null,
): { id: number; name: string; image: any }[] => {
  const sortedPlans = [...homePlanData].sort((a, b) => {
    if (designStudioId) {
      return (
        Number(!!b.FloorPlans?.find((fp) => fp.IsPreferred)) -
        Number(!!a.FloorPlans?.find((fp) => fp.IsPreferred))
      );
    }

    return Number(b.IsPreferred) - Number(a.IsPreferred);
  });

  return sortedPlans.map((plan) => ({
    id: plan.ExteriorStyle?.Id,
    name: plan.ExteriorStyle?.Name,
    image: {
      src: {
        desktop: plan.Image,
        tablet: plan.Image,
        mobile: plan.Image,
      },
      alt: `${plan.ExteriorStyle?.Name} Exterior Image`,
    },
  }));
};

export const useHomePlanData = (planId: number) => {
  const { studioId, hasLoadedLocation } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [planDetailData, setPlanDetailData] = useState<IHousePlanHit[]>([]);
  const [baseElevation, setBaseElevation] = useState<IHousePlanHit | null>(
    null,
  );
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const [defaultFloorplanImages, setDefaultFloorplanImages] = useState<
    IFloorPlanImage[] | null
  >(null);

  const [selectedExterior, setSelectedExterior] =
    useState<IExteriorStyles | null>(null);
  const [availableExteriors, setAvailableExteriors] = useState<
    IExteriorStyles[]
  >([]);
  const [exteriorCount, setExteriorCount] = useState<number>(0);

  const [locationConflict, setLocationConflict] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const userSelectedExterior = useRef(false);

  useEffect(() => {
    if (!planId || !hasLoadedLocation) return;

    const cacheKey = `${planId}-${studioId ?? 'none'}`;
    const now = Date.now();

    // Use cache if available and not expired based on custom TTL
    if (
      homePlanCache[cacheKey] &&
      now - homePlanCache[cacheKey].timestamp < CACHE_TTL
    ) {
      setPlanDetailData(homePlanCache[cacheKey].data);
      setDefaultFloorplanImages(homePlanCache[cacheKey].fallbackImages ?? null);
      setIsLoading(false);
      return;
    }

    // Since all req happen at the same time if a fetch is already in progress for this cacheKey, wait for it
    if (homePlanFetchPromises[cacheKey]) {
      setIsLoading(true);
      homePlanFetchPromises[cacheKey]!.then((data) => {
        setPlanDetailData(data);
        setDefaultFloorplanImages(
          homePlanCache[cacheKey]?.fallbackImages ?? null,
        );
        setIsLoading(false);
      });
      return;
    }

    const fetchPlanDetailData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        params.append('home_plan_id', planId.toString());
        if (studioId !== null && studioId !== undefined) {
          params.append('location_id', studioId.toString());
        }

        const resp = await fetch(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/home-plans?${params.toString()}`,
          {
            next: { revalidate: 3600 },
          },
        );

        if (!resp.ok) {
          throw new Error(`Failed to fetch: ${resp.status} ${resp.statusText}`);
        }

        let data = (await resp.json()) as IHousePlanHit[];
        let fallbackImages: IFloorPlanImage[] | null = null;

        // Fallback to Cleveland-Akron (location_id=1) for floorplan images when no location is set
        if (studioId === null || studioId === undefined) {
          const fallbackParams = new URLSearchParams();
          fallbackParams.append('home_plan_id', planId.toString());
          fallbackParams.append('location_id', '1');

          const fallbackResp = await fetch(
            `${process.env.NEXT_PUBLIC_API_BASE_URL}/algolia/home-plans?${fallbackParams.toString()}`,
            {
              next: { revalidate: 3600 },
            },
          );

          if (fallbackResp.ok) {
            const fallbackData = (await fallbackResp.json()) as IHousePlanHit[];
            fallbackImages =
              fallbackData.find((plan) => plan?.IsBaseElevation)
                ?.FloorPlans?.[0]?.FloorPlanImages ?? null;
            setDefaultFloorplanImages(fallbackImages);

            if (data == null || data.length == 0) {
              // This should be rare but certain plans you won't get details right now without using fallback
              // we just need something to display for now
              data = fallbackData;
            }
          }
        }

        if (data.length === 0 && studioId !== null && studioId !== undefined) {
          setLocationConflict(true);
        }

        // Fetch from Contentstack and merge
        const allContentstackEntries: ITmHomePlanTemplateCSProps[] =
          (await getEntriesWithFilter({
            contentTypeUid: 'home_plan_detail',
            filters: [],
          })) as ITmHomePlanTemplateCSProps[];

        const contentstackData = allContentstackEntries.filter(
          (entry) => Number(entry.home_plan_id?.select) === planId,
        );

        const enriched = data.map((floorplan) => ({
          ...floorplan,
          supports3D: contentstackData[0]?.has_three_d,
        }));

        // Cache result with fallback images
        homePlanCache[cacheKey] = {
          data: enriched,
          timestamp: Date.now(),
          fallbackImages,
        };
        setPlanDetailData(enriched);
        return enriched;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        return [];
      } finally {
        setIsLoading(false);
        homePlanFetchPromises[cacheKey] = null;
      }
    };

    // Fetch promise so other hooks can await it
    homePlanFetchPromises[cacheKey] = fetchPlanDetailData();
    // If not awaited above, handle the result here
    homePlanFetchPromises[cacheKey]!.then((data) => {
      setPlanDetailData(data);
      setDefaultFloorplanImages(
        homePlanCache[cacheKey]?.fallbackImages ?? null,
      );
    });
  }, [planId, studioId, hasLoadedLocation]);

  useEffect(() => {
    if (!planDetailData || planDetailData.length === 0) return;

    const styles = getExteriorStyles(planDetailData, studioId);

    setExteriorCount(styles.length);

    if (styles.length > 0) {
      setSelectedExterior(styles[0]);
      setAvailableExteriors(styles.slice(1));
    }
  }, [planDetailData, studioId]);

  useEffect(() => {
    const base = planDetailData.find((plan) => plan.IsBaseElevation) ?? null;
    setBaseElevation(base);
  }, [planDetailData]);

  useEffect(() => {
    if (!planDetailData.length || !selectedExterior?.id || !studioId) {
      return setBasePrice(null);
    }

    const plan = planDetailData.find(
      (plan) => plan.ExteriorStyle.Id === selectedExterior.id,
    );

    const price = plan?.FloorPlans?.[0]?.BasePrice ?? null;
    setBasePrice((prev) => (prev !== price ? price : prev));
  }, [planDetailData, selectedExterior, studioId]);

  const handleSelectExterior = (newExterior: IExteriorStyles) => {
    if (!selectedExterior) return;

    userSelectedExterior.current = true;

    setAvailableExteriors((prev) => [
      selectedExterior,
      ...prev.filter((style) => style.id !== newExterior.id),
    ]);
    setSelectedExterior(newExterior);
  };

  return {
    planName: baseElevation?.Plan.Name,
    baseElevation,
    price: basePrice,
    defaultFloorplanImages,
    selectedExterior,
    availableExteriors,
    exteriorCount,
    handleSelectExterior,
    locationConflict,
    planDetailData,
    isLoading,
    supports3D: planDetailData?.[0]?.supports3D ?? false,
    error,
    userSelectedExterior,
  };
};
